/**
 * Widget V2 — Discovery shared bits
 *
 * Small, reusable pieces for the first-time-visitor discovery surfaces
 * (Home rails, Coaches, Locations, Services). The init payload resolves image
 * file IDs to URLs (trainer/facility/location/service `imgUrl`); avatars and
 * thumbs fall back to initials / category icons when a photo isn't uploaded.
 */

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";

import { useImageFallback } from "/imports/ui/components/shared";

export const DISCOVERY_CARD_SX = {
  borderRadius: 4,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 4px 20px rgba(26,26,26,0.04)",
  bgcolor: "#fff"
};

export const SECTION_LABEL_SX = {
  display: "block",
  fontWeight: 700,
  letterSpacing: "0.1em",
  fontSize: "0.7rem",
  color: "text.secondary",
  textTransform: "uppercase"
};

export function fullName(trainer) {
  return [trainer?.firstName, trainer?.lastName].filter(Boolean).join(" ").trim() || "Coach";
}

export function initialsOf(trainer) {
  return `${trainer?.firstName?.[0] || ""}${trainer?.lastName?.[0] || ""}`.toUpperCase() || "?";
}

/** Coach avatar — renders the photo (`trainer.imgUrl`) when available, falling
    back to initials on a brand tint. The fallback is deliberately one uniform
    color: initials are temporary placeholders until the club uploads photos,
    and the tint derives from the theme so custom-branded widgets stay coherent.
    An explicit `src` overrides (e.g. service-embedded coaches that don't carry
    the resolved URL). */
export function CoachAvatar({ trainer, size = 56, src }) {
  return (
    <Avatar
      src={src || trainer?.imgUrl || undefined}
      alt={fullName(trainer)}
      sx={{
        width: size,
        height: size,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.15),
        color: "primary.dark",
        fontWeight: 700,
        fontSize: size * 0.36
      }}
    >
      {initialsOf(trainer)}
    </Avatar>
  );
}

/** Back handler that returns the user to wherever they actually came from
    (e.g. Home), falling back to a fixed list page only on a fresh/deep-linked
    load with no in-app history. react-router stamps the first history entry with
    key "default", so anything else means there's somewhere to go back to. */
export function useBackTo(fallback) {
  const navigate = useNavigate();
  const location = useLocation();
  return () => {
    if (location.key && location.key !== "default") navigate(-1);
    else navigate(fallback);
  };
}

/** In-content page heading (the app's Layout already owns the top AppBar, so
    views render their own title inline rather than stacking a second bar).
    Mobile sizing matches the booking views' compact hero so the discovery and
    booking surfaces read as one app. */
export function PageHeading({ title, subtitle, onBack, action }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: { xs: 2, md: 3 } }}>
      {onBack && (
        <IconButton size="small" onClick={onBack} edge="start" aria-label="Go back" sx={{ mt: 0.25 }}>
          <ArrowBackIcon />
        </IconButton>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: { xs: "1.4rem", md: "1.5rem" }, fontWeight: { xs: 800, md: 700 }, letterSpacing: { xs: "-0.02em", md: "-0.01em" } }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography color="text.secondary" sx={{ mt: 0.25, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
  );
}

/** Title + "View all" row. Shared by the desktop rails and their mobile
    (stacked / grid) counterparts so both read as the same section. */
export function SectionHeader({ title, onViewAll }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
      <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: "-0.01em" }}>
        {title}
      </Typography>
      {onViewAll && (
        <Button size="small" endIcon={<ChevronRightIcon />} onClick={onViewAll} sx={{ textTransform: "none", fontWeight: 600 }}>
          View all
        </Button>
      )}
    </Box>
  );
}

/** A titled, horizontally-scrollable row used on the Home discovery landing.
    Desktop only — on mobile the rails lay their items out vertically instead,
    since a clipped side-scroller reads as broken on a narrow screen. */
export function Rail({ title, onViewAll, children }) {
  return (
    <Box sx={{ mb: 4 }}>
      <SectionHeader title={title} onViewAll={onViewAll} />
      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          pb: 1,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none"
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/** Maps a service category type to the booking route + the filter key to pass. */
export const CATEGORY_ROUTE = {
  "category-service-court": "courts",
  "category-service-private": "lessons",
  "category-service-class": "classes",
  "category-service-term": "terms",
  "category-service-package": "plans",
  "category-service-membership": "plans"
};

/** Category icons, matching the Home intent tiles so the two surfaces read as
    one visual language. */
export const CATEGORY_ICON = {
  "category-service-court": SportsTennisIcon,
  "category-service-private": SupervisorAccountOutlinedIcon,
  "category-service-class": Diversity3OutlinedIcon,
  "category-service-term": CalendarMonthIcon
};

/** Booking route for a service. A class from one of the club's standalone class
    categories (Events, Camps…) books from that category's own page, not the
    Group Classes timetable. */
export function bookingRoute(service) {
  if (service?.isStandaloneClassCategory && service.categoryId) return `events/${service.categoryId}`;
  return CATEGORY_ROUTE[service?.categoryType];
}

/** Booking deep-link for a service. Extra params (trainerId, duration…) map
    straight onto the booking views' URL filters via useUrlFilters. */
export function bookingHref(slug, service, extraParams = {}) {
  const route = bookingRoute(service);
  if (!route) return `/${slug}`;
  const params = new URLSearchParams();
  if (service.sportId) params.set("sport", service.sportId);
  // Lessons, courts, classes & events all filter by serviceId so the booking view
  // opens scoped to this exact service rather than its whole category.
  if (route === "lessons" || route === "courts" || route === "classes" || route.startsWith("events/")) params.set("serviceId", service._id);
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const qs = params.toString();
  return `/${slug}/${route}${qs ? `?${qs}` : ""}`;
}

/** Square service thumbnail — the club-uploaded photo when there is one, else
    the category icon on a brand tint (same tile as the Home intents). */
export function ServiceThumb({ service, size = 56, iconSize = 24, radius = 2.5 }) {
  const Icon = CATEGORY_ICON[service?.categoryType] || CategoryOutlinedIcon;
  const { showImage, onImageError } = useImageFallback(service?.imgUrl);

  if (showImage) {
    return (
      <Box
        component="img"
        src={service.imgUrl}
        alt={service.name || ""}
        onError={onImageError}
        sx={{ width: size, height: size, borderRadius: radius, objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: radius,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
        color: "primary.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
      }}
    >
      <Icon sx={{ fontSize: iconSize }} />
    </Box>
  );
}

export default {
  CoachAvatar,
  Rail,
  SectionHeader,
  PageHeading,
  useBackTo,
  fullName,
  initialsOf,
  CATEGORY_ROUTE,
  CATEGORY_ICON,
  bookingHref,
  bookingRoute,
  ServiceThumb,
  DISCOVERY_CARD_SX,
  SECTION_LABEL_SX
};
